/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { mutate } from "swr"

import { AddFavoriteListByPropertyIdRequest } from "commonModule/services/favorite/createFavoriteListByPropertyId"
import { AddFavouriteListRequest } from "commonModule/services/favorite/createFavouriteList"
import { GetUserFavListRequest } from "commonModule/services/favorite/getSpecificFavoriteListWithId"
import { RemoveFromFavlistRequest } from "commonModule/services/favorite/removePropertyFromFavList"
import { RenameFavoriteListRequest } from "commonModule/services/favorite/renameFavoriteList"
import { SharedFavouriteListRequest } from "commonModule/services/favorite/sharedFavoriteList"
import { UpdateFavoriteListRequest } from "commonModule/services/favorite/updateFavoriteList"
import { apiFavClient } from "services/apiFavClient"

import { FavouritesIntialState } from "./types"

export const updateFavoriteList = createAsyncThunk(
  "favouriteStore/addToFavouritelist",
  async (request: UpdateFavoriteListRequest) => {
    const response = await apiFavClient.updateFavouriteList(request)
    // get the updated fav. list & fav. properties ids
    void mutate("/user/favouritelist/all-properties-by-userid")
    void mutate(`/api/getFavoritePropertyIds`)

    return response.data
  },
)

export const addToFavoriteList = createAsyncThunk(
  "favouriteStore/addfavouritelist",
  async (
    {
      propertyId,
      previewsFavouriteListId,
      priceDropAlertId,
      propertyName,
      city,
      state,
      country,
      userName,
      ...request
    }: {
      city?: string
      country?: string
      propertyId: string
      previewsFavouriteListId: string | number | undefined
      priceDropAlertId: number | string
      propertyName: string
      state?: string
      userName: string
    } & AddFavouriteListRequest,
    { dispatch },
  ) => {
    const response = await apiFavClient.addToFavourites(request)
    //  update existing fav. list store
    const params = {
      favouriteListId: `${response.data.data.favoriteId}`,
      favouriteListName: response.data.data.favouriteName,
      propertyId,
      previewsFavouriteListId,
      priceDropAlertId,
    }

    setTimeout(() => {
      void (async () => {
        const mixpanelV1Client = (await import("services/mixpanel"))
          .mixpanelV1Client
        mixpanelV1Client.SAVE_TO_FAVOURITES({
          propertyId,
          propertyName,
          userName,
          city,
          state,
          country,
          isFavourite: true,
          isFavouriteList: true,
          favouriteListName: response.data.data.favouriteName,
          favouriteListId: response.data.data.favoriteId,
        })
      })()
    }, 0)
    void dispatch(updateFavoriteList({ params }))
    return response.data
  },
)

export const addToFavoriteListByPropertyId = createAsyncThunk(
  "favouriteStore/addToFavoriteListByPropertyId",
  async (request: AddFavoriteListByPropertyIdRequest) => {
    const propertyId = request.params.propertyId
    const response = await apiFavClient.createFavoriteListByPropertyId(request)

    setTimeout(() => {
      void (async () => {
        const mixpanelV1Client = (await import("services/mixpanel"))
          .mixpanelV1Client
        mixpanelV1Client.SAVE_TO_FAVOURITES({
          propertyId,
          isFavourite: true,
          isFavouriteList: true,
        })
      })()
    }, 0)
    // get the updated fav. list & fav. properties ids
    void mutate("/user/favouritelist/all-properties-by-userid")
    void mutate(`/api/getFavoritePropertyIds`)
    return response.data
  },
)

export const updateSharedFavoriteListThunk = createAsyncThunk(
  "favouriteStore/updateSharedFavoriteListThunk",
  async ({
    request,
    shouldCallgetAllFavoriteList = false,
  }: {
    request: SharedFavouriteListRequest
    shouldCallgetAllFavoriteList?: boolean
  }) => {
    const response = await apiFavClient.updateSharedFavoriteList(request)
    // fetch the specific fav. list details in store
    if (shouldCallgetAllFavoriteList) {
      void mutate("/user/favouritelist/all-properties-by-userid")
    }
    return response.data
  },
)

export const removeFromFavoriteListThunk = createAsyncThunk(
  "favouriteStore/removeFromFavoriteListThunk",
  async (request: RemoveFromFavlistRequest) => {
    const response = await apiFavClient.removeFromFavoriteList(request)
    // if removing from favorite action triggered from specific page then we got that favoriteId & we need to fetch fav. all list & specific fav. list
    if (request.params.favoriteId && request.params.favoriteId !== "0") {
      void mutate("/user/favouritelist/all-properties-by-userid")
      void mutate({
        key: "/api/getSpecificFavoriteList",
        favoriteListId: request.params.favoriteId,
      })
    }
    // otherwise action triggered from vacation-rental page ,we need to fetch only getFavoritePropertyIds
    else {
      void mutate(`/api/getFavoritePropertyIds`)
    }

    return response.data
  },
)

export const renameFavoriteListThunk = createAsyncThunk(
  "favouriteStore/renameFavoriteListThunk",
  async (request: RenameFavoriteListRequest) => {
    const response = await apiFavClient.renameFavouriteList(request)
    // get the updated fav. list & fav. properties ids
    void mutate("/user/favouritelist/all-properties-by-userid")
    void mutate(`/api/getFavoritePropertyIds`)
    return response.data
  },
)

const initialState: FavouritesIntialState = {
  error: null,
  loading: true,
}

export const favouriteSlice = createSlice({
  name: "favouriteStore",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(addToFavoriteList.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(addToFavoriteList.fulfilled, (state) => {
      state.loading = false
      state.error = null
      void mutate("/user/favouritelist/all-properties-by-userid")
    })
    builder.addCase(addToFavoriteList.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message as string
    })
    // ----------------------------------------------------------------------
    builder.addCase(updateFavoriteList.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(updateFavoriteList.fulfilled, (state) => {
      state.loading = false
      state.error = null
    })
    builder.addCase(updateFavoriteList.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message as string
    })

    // ----------------------------------------------------------------
    builder.addCase(updateSharedFavoriteListThunk.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(updateSharedFavoriteListThunk.fulfilled, (state) => {
      state.loading = false
      state.error = null
    })
    builder.addCase(updateSharedFavoriteListThunk.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message as string
    })
    // ----------------------------------------------------------------

    builder.addCase(removeFromFavoriteListThunk.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message as string
    })
    builder.addCase(removeFromFavoriteListThunk.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(removeFromFavoriteListThunk.fulfilled, (state) => {
      state.loading = false
      state.error = null
    })
    builder.addCase(renameFavoriteListThunk.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message as string
    })
    builder.addCase(renameFavoriteListThunk.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(renameFavoriteListThunk.fulfilled, (state) => {
      state.loading = false
      state.error = null
    })
  },
})

// export const {} = favouriteSlice.actions

export const deleteFavoriteListThunk = createAsyncThunk(
  "favouriteStore/deleteFavoriteListThunk",
  async (request: GetUserFavListRequest) => {
    const response = await apiFavClient.removeFavouriteList(request)
    //  get updated fav. list
    void mutate("/user/favouritelist/all-properties-by-userid")
    return response.data
  },
)

export default favouriteSlice.reducer
